// Core imports
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';

// Component imports
import { Button, Col, Divider, Row, Tabs, Tag, Typography } from 'antd';
import { ExportOutlined } from '@ant-design/icons';
import SideModal from '../Layout/SideModal';
import JsonViewer from './JsonViewer';
import VariablesList from './VariablesList';

// Misc imports
import { useFetchEventIdInteractionVariablesQuery } from '../../store/interactionMetadata/interactionMetadataApi';
import { NAV_CHAT_SESSIONS } from '../../constants/navigation';
import { DATETIME_BACKEND_FORMAT, INTERACTION_TYPE_FILTERS } from '../../constants/main';
import replaceUrlParams from '../../utils/string/replaceUrlParams';

const InteractionLogDetailModal = ({ interaction, open, onClose, timezone }) => {
  const { orgId } = useParams();
  const { t } = useTranslation();

  const { data: variables, isFetching: isFetchingVariables } = useFetchEventIdInteractionVariablesQuery(
    { orgId, eventId: interaction?.hbfEventId },
    { skip: !interaction?.hbfEventId || !open }
  );

  const viewInSessionUrl = useMemo(() => {
    if (!interaction) {
      return '';
    }
    const basePath = replaceUrlParams(NAV_CHAT_SESSIONS, { orgId });
    const interactionMoment = timezone
      ? moment(interaction.timestamp).tz(timezone)
      : moment(interaction.timestamp);
    const startDate = encodeURIComponent(interactionMoment.clone().subtract(1, 'day').startOf('day').format(DATETIME_BACKEND_FORMAT));
    const endDate = encodeURIComponent(interactionMoment.clone().endOf('day').format(DATETIME_BACKEND_FORMAT));
    const params = new URLSearchParams({
      startDate,
      endDate,
      selectedSessionId: interaction.sessionId,
      selectedInteractionId: interaction.hbfEventId,
      searchBy: 'uuid',
      uuid: interaction.sessionId
    });
    return `${basePath}?${params.toString()}`;
  }, [interaction, orgId, timezone]);

  const typeLabel = useMemo(
    () => INTERACTION_TYPE_FILTERS.find(item => item.value === interaction?.type)?.text || interaction?.type,
    [interaction?.type]
  );

  const formattedTimestamp = useMemo(() => {
    if (!interaction?.timestamp) {
      return '';
    }
    return timezone
      ? moment(interaction.timestamp).tz(timezone).format('YYYY-MM-DD HH:mm:ss.SSS')
      : moment(interaction.timestamp).format('YYYY-MM-DD HH:mm:ss.SSS');
  }, [interaction?.timestamp, timezone]);

  const tabItems = useMemo(() => {
    if (!interaction) {
      return [];
    }
    return [
      {
        key: 'payload',
        label: t('Payload'),
        children: (
          <div>
            {interaction.payload && Object.keys(interaction.payload).length > 0
              ? (
                <Row className="bg-layout-gray border-radius-base">
                  <Col xs={24} className="p-4">
                    <JsonViewer jsonObject={interaction.payload} />
                  </Col>
                </Row>
              )
              : <Typography.Text type="secondary">{t('No payload data')}</Typography.Text>
            }
          </div>
        )
      },
      {
        key: 'variables',
        label: t('Variables'),
        children: (
          <div>
            <VariablesList
              variables={variables}
              isLoading={isFetchingVariables}
              searchEnabled={variables && variables.length > 5}
            />
          </div>
        )
      }
    ];
  }, [interaction, variables, isFetchingVariables, t]);

  if (!interaction) {
    return null;
  }

  return (
    <SideModal
      open={open}
      onClose={onClose}
      title={t('Interaction Details')}
      destroyOnClose
      width={900}
      hasFullScreenButton
    >
      <div className="px-6 pt-4 flex-grow-1 h-100 overflow-y-auto">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <Typography.Title level={5} className="mb-0">{interaction.friendlyName || typeLabel}</Typography.Title>
          <Button
            type="link"
            icon={<ExportOutlined />}
            href={viewInSessionUrl}
            target="_blank"
          >
            {t('View in Session')}
          </Button>
        </div>

        <Row className="py-1">
          <Col xs={6}>
            <Typography.Text type="secondary" className="fw-600">{t('Type')}</Typography.Text>
          </Col>
          <Col xs={18}>
            <Tag color="processing">{typeLabel}</Tag>
          </Col>
        </Row>
        {interaction.hasError && (
          <Row className="py-1">
            <Col xs={6}>
              <Typography.Text type="secondary" className="fw-600">{t('Error')}</Typography.Text>
            </Col>
            <Col xs={18}>
              <Tag color="error">{t('Yes')}</Tag>
            </Col>
          </Row>
        )}
        <Row className="py-1">
          <Col xs={6}>
            <Typography.Text type="secondary" className="fw-600">{t('Timestamp')}</Typography.Text>
          </Col>
          <Col xs={18}>
            <Typography.Text>{formattedTimestamp}</Typography.Text>
          </Col>
        </Row>
        {interaction.duration !== undefined && interaction.duration !== null && (
          <Row className="py-1">
            <Col xs={6}>
              <Typography.Text type="secondary" className="fw-600">{t('Duration')}</Typography.Text>
            </Col>
            <Col xs={18}>
              <Typography.Text>{interaction.duration}ms</Typography.Text>
            </Col>
          </Row>
        )}
        <Row className="py-1">
          <Col xs={6}>
            <Typography.Text type="secondary" className="fw-600">{t('Session ID')}</Typography.Text>
          </Col>
          <Col xs={18}>
            <Typography.Text copyable>{interaction.sessionId}</Typography.Text>
          </Col>
        </Row>
        <Row className="py-1">
          <Col xs={6}>
            <Typography.Text type="secondary" className="fw-600">{t('Event ID')}</Typography.Text>
          </Col>
          <Col xs={18}>
            <Typography.Text copyable>{interaction.hbfEventId}</Typography.Text>
          </Col>
        </Row>

        <Divider className="my-3" />

        <Tabs items={tabItems} defaultActiveKey="payload" />
      </div>
    </SideModal>
  );
};

InteractionLogDetailModal.propTypes = {
  interaction: PropTypes.object,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  timezone: PropTypes.string
};

export default InteractionLogDetailModal;
